import { Checkbox, type CheckboxProps } from '@progress/kendo-react-inputs'
import { useAtom } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import React from 'react'
import * as R from 'remeda'
import { match } from 'ts-pattern'

import { VStack } from '~/components/elements/VStack'
import { useTurnOnAtom } from '~/hooks/jotai'
import { type CustomerAttributeGroup } from '~/hooks/useSurveyQuery'

import {
  columnCustomerAttributeIdsAtom,
  isPendingTabulationAtom,
  sortValueAtom,
} from '../atoms'

import { CustomerAttributeCheckbox } from './CustomerAttributeCheckbox'
import styles from './CustomerAttributeGroupCheckboxTree.css'

export type CustomerAttributeGroupCheckboxTreeProps = {
  customerAttributeGroup: CustomerAttributeGroup
}

export const CustomerAttributeGroupCheckboxTree = ({
  customerAttributeGroup,
}: CustomerAttributeGroupCheckboxTreeProps) => {
  const { customerAttributes, name } = customerAttributeGroup
  const customerAttributeIds = React.useMemo(
    () => customerAttributes.map(R.prop('id')),
    [customerAttributes],
  )
  const [columnCustomerAttributeIds, setColumnCustomerAttributeIds] = useAtom(
    columnCustomerAttributeIdsAtom,
  )
  const checked = React.useMemo(
    () =>
      match(
        R.intersection(customerAttributeIds, columnCustomerAttributeIds).length,
      )
        .with(0, () => false)
        .with(customerAttributeIds.length, () => true)
        .otherwise(() => null),
    [customerAttributeIds, columnCustomerAttributeIds],
  )

  const resetSortValue = useResetAtom(sortValueAtom)
  const suspendTabulation = useTurnOnAtom(isPendingTabulationAtom)
  const handleChange = React.useCallback<EH<CheckboxProps, 'onChange'>>(
    (event) => {
      resetSortValue()
      setColumnCustomerAttributeIds((prev) =>
        event.value
          ? R.unique(R.concat(prev, customerAttributeIds))
          : R.difference(prev, customerAttributeIds),
      )
      suspendTabulation()
    },
    [
      customerAttributeIds,
      resetSortValue,
      setColumnCustomerAttributeIds,
      suspendTabulation,
    ],
  )

  return (
    <VStack spacing={1}>
      <Checkbox
        checked={checked}
        label={name}
        labelClassName={styles.checkboxLabel}
        onChange={handleChange}
      />

      <VStack className={styles.customerAttributes} spacing={1}>
        {customerAttributes.map((customerAttribute) => (
          <CustomerAttributeCheckbox
            key={customerAttribute.id}
            customerAttribute={customerAttribute}
          />
        ))}
      </VStack>
    </VStack>
  )
}
